import { Bell, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { useFollowRoadmapItem } from "@/lib/api";
import { cn } from "@/lib/utils";

interface FollowButtonProps {
  itemId: string;
  /** The item's title, for the button's name: "Rapport per werf volgen". */
  title: string;
  /** Whether the viewer follows the item now. */
  following: boolean;
  /**
   * An icon with a tooltip in a table row, or a button with its label in the
   * details.
   */
  variant?: "icon" | "label";
  /** A failed follow or unfollow, for the page to say. */
  onError?: (error: unknown) => void;
  /** After a follow or unfollow went through. */
  onDone?: () => void;
}

/**
 * Follow an item or stop following it, as a button that stays pressed
 * (`aria-pressed`) while the viewer follows it: a bell, filled while
 * pressed. Nothing changes until the server answered and the page refetched.
 */
export function FollowButton({
  itemId,
  title,
  following,
  variant = "icon",
  onError,
  onDone,
}: FollowButtonProps) {
  const { t } = useTranslation();
  const follow = useFollowRoadmapItem();
  const toggle = () =>
    follow.mutate(
      { itemId, following: !following },
      { onError: (error) => onError?.(error), onSuccess: () => onDone?.() },
    );
  const icon = follow.isPending ? (
    <Loader2 aria-hidden="true" className="size-4 animate-spin" />
  ) : (
    <Bell aria-hidden="true" className={cn("size-4", following && "fill-current")} />
  );

  if (variant === "label") {
    return (
      <Button
        variant="outline"
        size="sm"
        aria-pressed={following}
        onClick={toggle}
        disabled={follow.isPending}
        className={cn(following && "border-primary bg-accent-light text-primary")}
      >
        {icon}
        {/* The name stays while the pressed state changes, as a toggle's should. */}
        {t("roadmap.followers.follow")}
      </Button>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={t("roadmap.followers.followItem", { item: title })}
          aria-pressed={following}
          onClick={toggle}
          disabled={follow.isPending}
          className={cn(
            "h-8 w-8 pointer-coarse:size-10",
            following ? "text-primary" : "text-muted",
          )}
        >
          {icon}
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {following ? t("roadmap.followers.unfollow") : t("roadmap.followers.follow")}
      </TooltipContent>
    </Tooltip>
  );
}
